import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { motion } from 'framer-motion';

import {
  useOpenContractMutation,
  useShufleCustomContractMutation,
} from '@/api/queries/contract';
import { useNextSpecContractTime } from '@/common/hooks/next-spec-contract-time';
import { SendAnalitics } from '@/common/lib/playdeck';
import { playSound } from '@/common/lib/soundEffect';
import {
  type ContractDataType,
  type PaidSpecContractType,
  type SpecContractType,
} from '@/common/types/contract';
import { ContractRewardsList } from '@/components/Contract/ContractRewardsList/ContractRewardsList';
import { OpenContractTimer } from '@/components/Contract/ContractTimer/OpenContractTimer';
import { getStatusBySpecContractData } from '@/components/Contract/lib/getStusByContractData';
import WarningTimer from '@/components/Contract/WarningTimer/WarningTimer';
import { ContractItem } from '@/components/ContractItem/ContractItem';
import {
  H1,
  H2,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';
import { ModalCharacterBackground } from '@/components/ui/Backgrounds/ModaCharacterBackground/ModalCharacterBackground';
import {
  GreyBlurSmButton,
  MainButton,
  PurpleButton,
  SecondaryButton,
} from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import VipPlateIcon from '@/components/ui/VipPlateIcon/VipPlateIcon';
import { addToBalance } from '@/redux/reducers/userReducer';
import { selectSpecContractTimeLeftByKind } from '@/redux/selectors/contractSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ModalContext } from '../../Modal/config';

import Tutorial from './Tutorial';

import './styles.scss';

import goldenFailPng from '/img/icons/golden_contract/Fail.png';
import goldenFailWebp from '/img/icons/golden_contract/Fail.webp';
import bgPng from '/img/modals/stars-1.png';
import bgWebp from '/img/modals/stars-1.webp';
import arrows from '/img/shop/boxes/arrows.svg';
import vipIcon from '/img/VIP/vip-icon.png';

interface GoldenProps {
  closeModal: () => void;
  seconds: number | undefined;
  nextGoldContract: number;
  contractData: ContractDataType;
  isAnimationStart: boolean;
  completeContractApiState: { isLoading: boolean };
  rejectContractApiState: { isLoading: boolean };
  hasContractCompleted: boolean;
  onComplete: () => void;
  onReject: () => void;
  statusVIP?: boolean;
  cost?: Record<'tgem' | 'cash', number>;
  tutorial?: 'Custom' | 'Golden';
}

const GoldenAndCustom: React.FC<GoldenProps> = ({
  closeModal,
  contractData,
  completeContractApiState,
  rejectContractApiState,
  hasContractCompleted,
  onComplete,
  onReject,
  statusVIP,
  cost,
  tutorial,
}) => {
  const { t } = useTranslation();
  // const [timerDuration, setTimerDuration] = useState<number>(seconds || 0);
  const [isOpen, setIsOpen] = useState<boolean>(!!contractData.isOpened);
  const timeToNextContract = useNextSpecContractTime(
    contractData.type as SpecContractType | PaidSpecContractType,
  );
  const hours = Math.floor((timeToNextContract || 0) / 3600);
  const time = useAppSelector((state) =>
    selectSpecContractTimeLeftByKind(state, contractData.type),
  );
  const [openContract] = useOpenContractMutation();
  const [shufleCustom, { isLoading }] = useShufleCustomContractMutation();
  const tgemBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const { openModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();

  const open = async (): Promise<void> => {
    if (contractData.type !== 'Custom') playSound('contracts/open');

    if (contractData.type === 'Custom' && !statusVIP) {
      if (!cost) return;

      if ((tgemBalance || 0) < cost.tgem) {
        // secondModal.openModal({ renderKind: 'coinDeposit', props: undefined });
        // closeModal();
        openModal({
          renderKind: 'coinDeposit',
          props: { source: 'take_contract_no_balance' },
        });

        return;
      }
      dispatch(addToBalance({ tgem: cost.tgem * -1 }));
    }
    await openContract({
      contractId: contractData.id,
      type: contractData.type,
    })
      .unwrap()
      .then((data) => {
        if (!data || 'link' in data || 'address' in data) {
          return;
        }

        if (contractData.type === 'Custom' && !statusVIP && cost) {
          SendAnalitics.coinSpend({
            amount: cost.tgem,
            type: 'open_contract',
            item_name: 'Custom',
          });
        }
        const contract = data.contracts.find(
          (contract) => contract.type === contractData.type,
        );
        setIsOpen(!!contract?.isOpened);

        // if (contract?.type === 'Golden')
        //   setTimerDuration((contract?.timeLeft || 0) / 1000);
      })
      .catch((e) => console.error(e));
  };

  const isGoldenContract: boolean =
    contractData.type === 'Golden' || contractData.type === 'GoldenPlus';

  const isGoldenContractFail: boolean = !!(
    isGoldenContract &&
    typeof time === 'number' &&
    time <= 0 &&
    isOpen
  );

  const contractStatus = getStatusBySpecContractData(contractData);

  return (
    <div className={cn('goldenModal', isGoldenContract ? 'golden' : 'custom')}>
      {!isGoldenContract && !statusVIP && !tutorial && (
        <VipPlateIcon classNames="rounded-tl-[16px] rounded-tr-[0px] rounded-br-[16px] rounded-bl-[0px] justify-center py-[8px] gap-[4px] absolute top-0 left-0">
          <span>{t('Free for')}</span>
        </VipPlateIcon>
      )}
      {isGoldenContractFail && (
        <div className="fail">
          <motion.div
            initial={{ opacity: 0, scale: 1.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <picture>
              <source srcSet={goldenFailWebp} type="image/webp" />
              <img src={goldenFailPng} alt="character" />
            </picture>
          </motion.div>
        </div>
      )}
      <Close
        onClick={closeModal}
        className="absolute right-4 top-4 z-[9]"
        imageVariant="modal"
        variant="modal"
      />
      <div className="absolute top-0 left-0 mask-gradient h-[70%] w-[100%]">
        <ModalCharacterBackground
          backgroundImage={{ png: bgPng, webp: bgWebp }}
          classNames={{ wrapper: 'h-[100%]' }}
        />
      </div>
      <div className={cn('w-full h-full flex flex-col items-center mt-8')}>
        <H1
          className={cn(isGoldenContract && 'mt-7')}
          weight="bold"
          style={{ position: 'relative' }}
        >
          {contractData.type === 'Custom' && t('Custom contract')}
          {contractData.type === 'Golden' && t('Premium Contract')}
          {contractData.type === 'GoldenPlus' && t('Premium+ Contract')}
        </H1>
        <>
          {tutorial ? (
            <Tutorial tutorial={tutorial} />
          ) : (
            <>
              {isGoldenContract && (
                <OpenContractTimer
                  isExtendsTimer={contractStatus === 'expired'}
                  kind={contractData.type as SpecContractType}
                />
              )}
              <div
                className="h-[5.5rem] mx-auto w-[23.5rem] mt-9"
                style={{ opacity: isGoldenContractFail ? '0.6' : '1' }}
              >
                <ContractItem
                  {...contractData}
                  inModal={true}
                  isOpenedGolden={!isOpen}
                  className="!grid-cols-4 !grid-rows-1"
                />
              </div>
              <div className="justify-center flex flex-col items-center self-stretch">
                {!isGoldenContract && (
                  <>
                    <StringSmall className="block w-full text-center text-white/80 mt-4">
                      {t('Tap to keep component')}
                    </StringSmall>
                    <GreyBlurSmButton
                      className={cn('addButton mt-4 !w-40')}
                      text={t('Shuffle')}
                      isLoading={isLoading}
                      onClick={() => {
                        playSound('buttons/shuffle');
                        shufleCustom({ data: contractData.data });
                      }}
                    >
                      <img src={arrows} alt="arrows" width={16} />
                    </GreyBlurSmButton>
                  </>
                )}
                {((isOpen && isGoldenContract) || !isGoldenContract) && (
                  <div
                    className="flex flex-col items-center gap-2 mt-8"
                    style={{ opacity: isGoldenContractFail ? '0.6' : '1' }}
                  >
                    <H2
                      weight="bold"
                      className="!text-white extra-reward-title"
                    >
                      {t('Extra Reward!')}
                      <span>{isGoldenContract ? '+50%' : '+20%'}</span>
                    </H2>
                    <StringSmall weight="regular" className="!text-white/60">
                      {t('Complete the contract and get:')}
                    </StringSmall>
                  </div>
                )}
                {!isOpen &&
                  isGoldenContract &&
                  (hours > 3 ? (
                    <div className="flex flex-col items-center gap-2 px-[12%] text-center mt-[78px]">
                      <H2
                        weight="bold"
                        className="!text-white extra-reward-title"
                      >
                        {t('Ready for a Premium Contract?')}
                      </H2>
                      <StringSmall
                        weight="regular"
                        className="!text-white/80 !leading-1.3"
                      >
                        {t(
                          'Complete it before the timer runs out to claim valuable rewards!',
                        )}
                      </StringSmall>
                    </div>
                  ) : (
                    <WarningTimer
                      timer={timeToNextContract || 0}
                      classNames="mt-[40px]"
                    />
                  ))}
                <ContractRewardsList
                  kind={contractData.type}
                  className="mt-5"
                />
              </div>
              <div
                className={cn(
                  'w-full flex gap-2 px-4 relative z-[9] mb-[40px]',
                  isOpen && '!mt-[32px]',
                  isGoldenContract ? 'mt-[88px]' : 'mt-[32px] ',
                )}
              >
                {isOpen ? (
                  typeof time === 'number' && time <= 0 && isGoldenContract ? (
                    <SecondaryButton
                      isLoading={rejectContractApiState.isLoading}
                      disabled={
                        completeContractApiState.isLoading ||
                        rejectContractApiState.isLoading
                      }
                      onClick={onReject}
                      text={t('Remove')}
                    />
                  ) : (
                    <MainButton
                      isLoading={completeContractApiState.isLoading}
                      disabled={
                        completeContractApiState.isLoading ||
                        rejectContractApiState.isLoading ||
                        !hasContractCompleted
                      }
                      onClick={onComplete}
                      inactive={!hasContractCompleted}
                      text={t('Complete')}
                    ></MainButton>
                  )
                ) : isGoldenContract ? (
                  <>
                    <SecondaryButton onClick={closeModal} text={t('Later')} />
                    <MainButton onClick={open} text={t('Open')} />
                  </>
                ) : (
                  <PurpleButton
                    onClick={open}
                    className="relative !bg-black/20flex items-center gap-1 isolate justify-center"
                  >
                    <StringMain weight="bold">
                      {t('Open contract for')}
                    </StringMain>
                    {!statusVIP && contractData.cost ? (
                      Object.entries(contractData.cost).map(([key, value]) => (
                        <div key={key} className="flex gap-[4px] items-center">
                          <CurrencyImage
                            kind={key as 'tgem' | 'cash'}
                            size="1.25rem"
                          />
                          <StringMain
                            weight="extrabold"
                            className={cn('huge-text')}
                          >
                            {value}
                          </StringMain>
                        </div>
                      ))
                    ) : (
                      <div className="flex gap-[4px] items-center">
                        <img src={vipIcon} alt="vipIcon" width={24} />
                        <StringMain weight="bold">{t('Free')}</StringMain>
                      </div>
                    )}
                  </PurpleButton>
                )}
              </div>
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default GoldenAndCustom;
